import { useValueChanged } from "@/hooks/useValueChanged";
import { buttonVariants } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { type DateRange, DayPicker } from "react-day-picker";

// Additional props for our extended Calendar component
interface TimeSelectionProps {
  enableTimeSelection?: boolean;
  onTimeChange?: (times: { fromTime: string; toTime: string }) => void;
  timeRange?: {
    fromTime?: string;
    toTime?: string;
  };
}

// Combine the DayPicker props with our custom props
type CalendarProps = Omit<
  React.ComponentProps<typeof DayPicker>,
  "mode" | "selected" | "onSelect"
> &
  TimeSelectionProps & {
    selected?: DateRange | undefined;
    onSelect?: (date: DateRange | undefined) => void;
  };

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  enableTimeSelection = false,
  onTimeChange,
  timeRange,
  selected,
  onSelect,
  ...props
}: CalendarProps) {
  // Time states with reasonable defaults
  const [fromTime, setFromTime] = React.useState(
    timeRange?.fromTime || "00:00",
  );
  const [toTime, setToTime] = React.useState(timeRange?.toTime || "23:59");

  if (useValueChanged(timeRange)) {
    if (timeRange?.fromTime) setFromTime(timeRange.fromTime);
    if (timeRange?.toTime) setToTime(timeRange.toTime);
  }

  // Simple pass-through onSelect handler - parent component handles timezone conversion
  const handleSelect = React.useCallback(
    (newRange: DateRange | undefined) => {
      if (onSelect) {
        onSelect(newRange);
      }
    },
    [onSelect],
  );

  // Handle from time change
  const handleFromTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFromTime = e.target.value;
      setFromTime(newFromTime);

      // Notify parent about time change
      if (onTimeChange) {
        onTimeChange({ fromTime: newFromTime, toTime });
      }
    },
    [toTime, onTimeChange],
  );

  // Handle to time change
  const handleToTimeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newToTime = e.target.value;
      setToTime(newToTime);

      // Notify parent about time change
      if (onTimeChange) {
        onTimeChange({ fromTime, toTime: newToTime });
      }
    },
    [fromTime, onTimeChange],
  );

  const footerText = enableTimeSelection
    ? "All times are in UTC"
    : "All dates are in UTC (00:00 to 23:59:59)";

  return (
    <div>
      <style jsx global>{`
        input[type="time"]::-webkit-calendar-picker-indicator {
          opacity: 0;
          pointer-events: none;
          display: none;
        }
        input[type="time"]::-webkit-inner-spin-button {
          display: none;
        }
        input[type="time"]::-webkit-clear-button {
          display: none;
        }
        /* Firefox */
        input[type="time"] {
          appearance: textfield;
          -moz-appearance: textfield;
        }
        /* Override fixed height from button variants for calendar day buttons */
        .rdp.rdp button[class*="day"],
        .rdp.rdp button[class*="day"]:hover,
        .rdp.rdp button[class*="day"]:focus,
        .rdp.rdp button[class*="day"]:focus-visible,
        .rdp.rdp button[class*="day"]:active,
        .rdp.rdp button[class*="day"][aria-selected],
        .rdp.rdp button[class*="day"][aria-selected]:hover,
        .rdp.rdp button[class*="day"][aria-selected]:focus,
        .rdp.rdp button[class*="day-range-middle"],
        .rdp.rdp button[class*="day-range-middle"]:hover,
        .rdp.rdp button[class*="day-range-middle"]:focus,
        .rdp.rdp button[class*="day-range-middle"]:active,
        .rdp.rdp button[class*="day-range-middle"][aria-selected],
        .rdp.rdp button[class*="day-range-middle"][aria-selected]:hover,
        .rdp.rdp button[class*="day-range-middle"][aria-selected]:focus {
          height: 28px !important;
          min-height: 28px !important;
          max-height: 28px !important;
          border-radius: 0.375rem !important;
        }
        .rdp button[class*="day"]:hover,
        .rdp button[class*="day-range-middle"]:hover {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
      `}</style>
      <DayPicker
        mode="range"
        showOutsideDays={showOutsideDays}
        className={cn("p-3 type-body-s-regular", className)}
        classNames={{
          months:
            "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption:
            "flex justify-center pt-1 relative items-center overflow-visible",
          caption_label: "type-body-s-regular",
          nav: "space-x-1 flex items-center overflow-visible",
          nav_button:
            "inline-flex items-center justify-center h-8 w-8 bg-neutral-100 dark:bg-neutral-800 p-0 opacity-70 hover:opacity-100 rounded-md border border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 cursor-pointer z-50 transition-opacity disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)] focus-visible:ring-offset-2",
          nav_button_previous: "absolute left-0 z-50",
          nav_button_next: "absolute right-0 z-50",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell:
            "text-neutral-500 rounded-md w-7 type-body-s-regular dark:text-neutral-400",
          row: "flex w-full mt-2",
          cell: "h-7 w-7 text-center p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-neutral-100/50 [&:has([aria-selected])]:bg-neutral-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-neutral-900/50 dark:[&:has([aria-selected])]:bg-[var(--color-surface)]",
          day: cn(
            buttonVariants({ variant: "subtle" }),
            "h-7 w-7 p-0 type-body-s-regular aria-selected:opacity-100 rounded-md !h-7 !min-h-7 !max-h-7",
          ),
          day_range_start: cn(
            "day-range-start",
            buttonVariants({ variant: "default" }),
            "!h-7 !min-h-7 !max-h-7 !p-0 !rounded-md type-body-s-regular",
            "!bg-[#313435] !text-white hover:!bg-[#161718] dark:!bg-[#f5f5f5] dark:!text-black dark:hover:!bg-[#ffffff] dark:hover:!text-black",
            "!border-2 !border-transparent dark:hover:!border-transparent",
          ),
          day_range_end: cn(
            "day-range-end",
            buttonVariants({ variant: "default" }),
            "!h-7 !min-h-7 !max-h-7 !p-0 !rounded-md type-body-s-regular",
            "!bg-[#313435] !text-white hover:!bg-[#161718] dark:!bg-[#f5f5f5] dark:!text-black dark:hover:!bg-[#ffffff] dark:hover:!text-black",
            "!border-2 !border-transparent dark:hover:!border-transparent",
          ),
          day_selected:
            "bg-primary text-white hover:bg-primary hover:text-neutral-50 focus:bg-primary focus:text-neutral-50 dark:bg-primary dark:text-black dark:hover:text-black dark:focus:bg-primary dark:focus:text-black",
          day_today:
            "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50",
          day_outside:
            "day-outside text-neutral-500 opacity-50 aria-selected:bg-neutral-100/50 aria-selected:text-neutral-500 aria-selected:opacity-30 dark:text-neutral-400 dark:aria-selected:bg-[var(--color-surface)]/50 dark:aria-selected:text-neutral-400",
          day_disabled: "text-neutral-500 opacity-50 dark:text-neutral-400",
          day_range_middle:
            "aria-selected:bg-neutral-100 aria-selected:text-neutral-900 dark:aria-selected:bg-[var(--color-surface)] dark:aria-selected:text-neutral-50",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft: () => (
            <ChevronLeft className="w-5 h-5 pointer-events-none" />
          ),
          IconRight: () => (
            <ChevronRight className="w-5 h-5 pointer-events-none" />
          ),
        }}
        selected={selected}
        onSelect={handleSelect}
        {...props}
      />

      {enableTimeSelection && (
        <div className="px-4 grid grid-cols-2 gap-3 w-full">
          <div className="flex flex-col w-full">
            <input
              type="time"
              id="start-time"
              value={fromTime}
              onChange={handleFromTimeChange}
              className={cn(
                "h-10 w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 type-body-s-regular",
                "focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
                "dark:border-[var(--color-border)] dark:bg-neutral-950/30 dark:text-neutral-50",
              )}
            />
          </div>

          <div className="flex flex-col w-full">
            <input
              type="time"
              id="end-time"
              value={toTime}
              onChange={handleToTimeChange}
              className={cn(
                "h-10 w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 type-body-s-regular",
                "focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
                "dark:border-[var(--color-border)] dark:bg-neutral-950/30 dark:text-neutral-50",
              )}
            />
          </div>
        </div>
      )}
      <div className="px-3 pb-2 type-body-s-regular text-neutral-500 text-center mt-2">
        {footerText}
      </div>
    </div>
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
